import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { IconButton, Typography, Box, LinearProgress, Slider } from '@material-ui/core';
import { PlayArrow, Pause, ErrorOutline, Speed } from '@material-ui/icons';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import audioSequenceManager from '../../../utils/audioSequenceManager';
import MessageAck from "./MessageAck";
import { useMessageAckStyles } from "../styles/messageAckStyles";
import { useAudioPlayer } from "../../../context/AudioPlayerContext";

const useStyles = makeStyles((theme) => ({
  // 🎵 Container principal do player
  audioPlayerContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    minWidth: '280px',
    maxWidth: '350px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '12px',
    transition: 'all 0.2s ease',
  },

  // Hover seguindo o padrão das bolhas
  audioHoverRight: {
    '&:hover': {
      transform: 'translateX(2px)',
      boxShadow: (theme.palette?.type === 'dark' || theme.palette?.mode === 'dark')
        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
        : '0 2px 8px rgba(0, 0, 0, 0.12)',
    },
  },
  audioHoverLeft: {
    '&:hover': {
      transform: 'translateX(-2px)',
      boxShadow: (theme.palette?.type === 'dark' || theme.palette?.mode === 'dark')
        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
        : '0 2px 8px rgba(0, 0, 0, 0.12)',
    },
  },

  // 🎯 Botão de play/pause moderno
  playButton: {
    width: '48px',
    height: '48px',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      transform: 'scale(1.05)',
      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
    '& svg': {
      fontSize: '24px',
    },
    '&:disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
      transform: 'none',
      boxShadow: 'none',
    },
  },

  // 🌊 Container dos controles
  controlsContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  // 🎚️ Container do progresso
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  // 🎚️ Barra de progresso customizada
  progressBar: {
    flex: 1,
    height: '6px',
    borderRadius: '3px',
    backgroundColor: theme.palette.action.hover,
    cursor: 'pointer',
    overflow: 'hidden',
    position: 'relative',
    transition: 'height 0.2s ease',
    '&:hover': {
      height: '8px',
    },
  },

  progressFill: {
    height: '100%',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '3px',
    transition: 'width 0.1s ease',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: '-6px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '12px',
      height: '12px',
      backgroundColor: theme.palette.primary.main,
      borderRadius: '50%',
      opacity: 0,
      transition: 'opacity 0.2s ease',
    },
  },

  progressBarHover: {
    '& $progressFill::after': {
      opacity: 1,
    },
  },

  // ⏱️ Container inferior com tempo e controles
  bottomControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  // ⏱️ Texto do tempo
  timeText: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
    fontWeight: 500,
  },

  // 🎛️ Container de controles secundários
  secondaryControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },

  // ⚡ Botão de velocidade
  speedButton: {
    fontSize: '10px',
    padding: '4px 8px',
    minWidth: 'auto',
    height: '24px',
    borderRadius: '12px',
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    fontWeight: 600,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      transform: 'scale(1.05)',
    },
  },

  //  Container de loading
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: theme.palette.text.secondary,
  },

  // ❌ Container de erro
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: theme.palette.error.main,
    fontSize: '12px',
  },

  // 🌊 Visualização de ondas simples (CSS puro)
  waveVisualization: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    height: '20px',
    flex: 1,
    margin: '0 8px',
  },

  waveBar: {
    width: '3px',
    backgroundColor: theme.palette.action.hover,
    borderRadius: '2px',
    transition: 'all 0.3s ease',
  },

  waveBarActive: {
    backgroundColor: theme.palette.primary.main,
  },
}));

/**
 * 🎵 WhatsAppAudioPlayer - Player de áudio moderno e minimalista
 * 
 * Características:
 * - Design moderno com Material-UI
 * - Controles intuitivos e responsivos
 * - Visualização de progresso customizada
 * - Controle de velocidade integrado
 * - Suporte para diferentes formatos de áudio
 * - Animações suaves e microinterações
 * - Zero dependências externas de waveform
 */
const WhatsAppAudioPlayer = ({ message, messageIndex = 0, formatMessageTime, buildAudioQueue }) => {
  const classes = useStyles();
  const theme = useTheme();
  const ackClasses = useMessageAckStyles();
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const globalPlayer = useAudioPlayer();
  
  // 🎵 Estados do player (locais por padrão; quando acoplado ao global, usamos o contexto)
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [isChangingSpeed, setIsChangingSpeed] = useState(false);

  // 🎯 Detecção de dispositivos móveis (lógica do AudioModal)
  const isIOS = useMemo(() => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream, []);
  const isSafari = useMemo(() => /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent), []);
  
  // 🎯 URL do áudio com correção de porta e formato
  const audioUrl = useMemo(() => {
    const normalizeMediaUrl = (url) => {
      if (!url) return url;
      let u = url;
      // Dev: ajustar localhost:443 -> 8080
      if (u.includes('localhost:443/')) {
        u = u.replace('localhost:443/', 'localhost:8080/');
      }
      try {
        const loc = window.location;
        const isHttpsPage = loc.protocol === 'https:';
        const urlObj = new URL(u, loc.origin);
        // Se vier caminho relativo de /public, ancora no mesmo host
        if (u.startsWith('/public/')) {
          urlObj.protocol = loc.protocol;
          urlObj.host = loc.host;
        }
        // Em páginas https, promova http->https para evitar mixed content
        if (isHttpsPage && urlObj.protocol === 'http:') {
          urlObj.protocol = 'https:';
        }
        return urlObj.toString();
      } catch {
        return u;
      }
    };

    let correctedUrl = normalizeMediaUrl(message.mediaUrl);
    // Para iOS, força MP3
    if (isIOS) {
      correctedUrl = correctedUrl.replace('.ogg', '.mp3');
    }

    return correctedUrl;
  }, [message.mediaUrl, isIOS, isSafari]);

  // 🎯 Função para obter source de áudio (lógica melhorada)
  const getAudioSource = useCallback(() => {
    let sourceUrl = audioUrl;
    
    // Para iOS/Safari, força MP3
    if (isIOS || isSafari) {
      sourceUrl = sourceUrl.replace(".ogg", ".mp3");
      return <source src={sourceUrl} type="audio/mp3" />;
    }
    
    // Para outros browsers (PC), mantém OGG como principal
    return <source src={sourceUrl} type="audio/ogg" />;
  }, [audioUrl, isIOS, isSafari]);

  // 🕐 Formatador de tempo
  const formatTime = useCallback((time) => {
    if (!time || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // 🎯 Inicialização do playbackRate do localStorage
  useEffect(() => {
    const savedRate = localStorage.getItem('audioPlaybackRate');
    if (savedRate) {
      const rate = parseFloat(savedRate);
      if (rate && rate !== playbackRate) {
        setPlaybackRate(rate);
      }
    }
  }, []); // Executa apenas uma vez na montagem

  // Determina se este bubble representa a faixa atual do player global
  const isCurrentGlobalTrack = !!(globalPlayer?.currentTrack && (
    globalPlayer.currentTrack.id === message.id
  ));

  // Quando acoplado ao global, espelhar estado do contexto
  useEffect(() => {
    if (!isCurrentGlobalTrack || !globalPlayer) return;
    setIsLoading(false);
    setLoadError(null);
    // Espelha estado
    setIsPlaying(!!globalPlayer.isPlaying);
    setDuration(globalPlayer.duration || 0);
    setCurrentTime(globalPlayer.currentTime || 0);
  }, [isCurrentGlobalTrack, globalPlayer?.isPlaying, globalPlayer?.duration, globalPlayer?.currentTime]);

  // 🎯 Inicialização do áudio (somente quando não acoplado ao global)
  useEffect(() => {
    if (isCurrentGlobalTrack) {
      // Quando acoplado ao global, não registra player local para evitar colisão de estados
      return () => {};
    }

    const audio = audioRef.current;
    if (!audio) return;

    // Para PC, força recarregamento do áudio
    if (!isIOS && !isSafari) {
      audio.load();
    }

    // Registra este áudio no gerenciador de sequência (somente quando não for o global)
    audioSequenceManager.registerAudio(message.id, audioRef, messageIndex, message.fromMe);

    const handleLoadStart = () => setIsLoading(true);
    const handleLoadedData = () => {
      setIsLoading(false);
      setDuration(audio.duration || 0);
    };
    const handleError = (e) => {
      setIsLoading(false);
      setLoadError('Erro ao carregar áudio');
      console.error('❌ Erro no áudio:', e);
    };
    const handleTimeUpdate = () => {
      if (!isDragging) {
        setCurrentTime(audio.currentTime || 0);
      }
    };
    const handlePlay = () => {
      setIsPlaying(true);
      // Notifica o gerenciador que este áudio começou a tocar
      audioSequenceManager.startAudio(message.id);
    };
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      // Notifica o gerenciador para tentar reproduzir o próximo áudio
      audioSequenceManager.onAudioEnded(message.id);
    };

    // Event listeners
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('error', handleError);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    // Configurações iniciais
    audio.playbackRate = playbackRate;

    // 🎯 Para iOS/Safari, força o carregamento dos metadados (lógica do AudioModal)
    if (isIOS || isSafari) {
      audio.load();
    }

    return () => {
      // Remove event listeners
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      
      // Desregistra do gerenciador de sequência
      audioSequenceManager.unregisterAudio(message.id);
    };
  }, [audioUrl, isDragging, isIOS, isSafari, message.id, messageIndex, isCurrentGlobalTrack]);

  // 🎯 Toggle play/pause
  const handoffToGlobal = useCallback(() => {
    if (!globalPlayer) return false;
    try {
      // Se houver algo tocando no global e for de outra conversa, o handoff irá substituir a fila
      // Garante parada/pausa do local antes do handoff para não gerar duplo áudio
      try { audioSequenceManager.pauseAll(); } catch {}
      try { if (audioRef.current && !audioRef.current.paused) audioRef.current.pause(); } catch {}
      let tracks, startIndex;
      if (typeof buildAudioQueue === 'function') {
        const res = buildAudioQueue(message.id);
        tracks = res?.tracks;
        startIndex = res?.startIndex ?? 0;
      }

      // Fallback: fila unitária com o áudio atual
      if (!tracks || !tracks.length) {
        const isUuid = (v) => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
        const routeMatch = window.location?.pathname?.match(/\/tickets\/([0-9a-fA-F-]{36})/);
        const routeUuid = routeMatch && isUuid(routeMatch[1]) ? routeMatch[1] : null;
        const msgUuid = message.ticket?.uuid || message.ticketUuid || null;
        const useUuid = isUuid(msgUuid) ? msgUuid : (routeUuid || null);
        tracks = [{
          id: message.id,
          url: audioUrl,
          title: message.filename || message.originalName || 'Áudio',
          ticketId: message.ticketId,
          ticketUuid: useUuid,
          ticketRouteId: useUuid,
          contactName: message.contact?.name,
          index: 0,
        }];
        startIndex = 0;
      }

      globalPlayer.handoffFromElement(audioRef.current, tracks, startIndex);
      return true;
    } catch (e) {
      console.error('Handoff global falhou:', e);
    }
    return false;
  }, [globalPlayer, buildAudioQueue, message.id, message.filename, message.originalName, message.ticketId, message.contact?.name, audioUrl]);

  const togglePlayPause = useCallback(() => {
    // Se este bubble representa a faixa atual do player global, apenas alterna play/pause no global
    if (isCurrentGlobalTrack && globalPlayer) {
      if (globalPlayer.isPlaying) globalPlayer.pause(); else globalPlayer.play();
      return;
    }

    // Prioriza SEMPRE o player global para evitar players duplicados e travamentos
    // 1) Pausa quaisquer players locais ativos desta lista
    try { audioSequenceManager.pauseAll(); } catch {}
    // 2) Garante que o elemento local não continue tocando
    const audio = audioRef.current;
    try { if (audio && !audio.paused) audio.pause(); } catch {}

    // 3) Faz handoff para o player global com a fila correta desta conversa
    const handed = handoffToGlobal();
    if (handed) {
      // playQueue no contexto já inicia a reprodução; não precisamos chamar play() aqui
      return;
    }

    // 4) Fallback: se não conseguir handoff (alguma falha), usa player local
    if (!audio || isLoading || loadError) return;
    if (isPlaying) audio.pause(); else audio.play().catch(() => setLoadError('Erro na reprodução'));
  }, [isPlaying, isLoading, loadError, isCurrentGlobalTrack, globalPlayer, handoffToGlobal]);

  // Handoff removido daqui para evitar chamadas múltiplas em desmontes de várias bolhas

  // 🎚️ Controle de progresso
  const handleProgressClick = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * (duration || 0);
    if (isCurrentGlobalTrack && globalPlayer) {
      globalPlayer.seek(newTime);
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration, isCurrentGlobalTrack, globalPlayer]);

  // ⚡ Controle de velocidade com proteção contra cliques rápidos
  const cyclePlaybackRate = useCallback(() => {
    if (isCurrentGlobalTrack && globalPlayer) {
      const rates = [0.5, 1, 1.25, 1.5, 2];
      const current = globalPlayer.playbackRate || 1;
      const idx = rates.indexOf(current);
      const next = rates[(idx + 1) % rates.length];
      try { globalPlayer.setRate(next); } catch {}
      setPlaybackRate(next);
      localStorage.setItem('audioPlaybackRate', String(next));
      return;
    }
    if (isChangingSpeed) return; // Previne cliques múltiplos rápidos
    
    setIsChangingSpeed(true);
    
    const rates = [0.5, 1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    
    setPlaybackRate(nextRate);
    
    // Aplica diretamente ao áudio sem depender do useEffect
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
    
    localStorage.setItem('audioPlaybackRate', nextRate.toString());
    
    // Libera após um pequeno delay
    setTimeout(() => setIsChangingSpeed(false), 150);
  }, [playbackRate, isChangingSpeed, isCurrentGlobalTrack, globalPlayer]);

  //  Cálculo do progresso
  const progressPercent = useMemo(() => {
    const dur = isCurrentGlobalTrack ? (globalPlayer?.duration || duration) : duration;
    const cur = isCurrentGlobalTrack ? (globalPlayer?.currentTime || currentTime) : currentTime;
    return dur > 0 ? (cur / dur) * 100 : 0;
  }, [currentTime, duration, isCurrentGlobalTrack, globalPlayer?.duration, globalPlayer?.currentTime]);

  // 🌊 Gerador de barras de visualização (CSS puro)
  const generateWaveBars = useCallback(() => {
    const bars = [];
    const barCount = 30;
    
    for (let i = 0; i < barCount; i++) {
      const height = Math.random() * 16 + 4; // Entre 4px e 20px
      const isActive = i < (barCount * progressPercent / 100);
      
      bars.push(
        <div
          key={i}
          className={`${classes.waveBar} ${isActive ? classes.waveBarActive : ''}`}
          style={{ height: `${height}px` }}
        />
      );
    }
    
    return bars;
  }, [classes, progressPercent]);

  // 🎯 Cores de fundo alinhadas ao padrão: enviado (verde), recebido (neutro)
  const containerBg = useMemo(() => {
    const isDark = (theme.palette?.type === 'dark' || theme.palette?.mode === 'dark');
    // Verde WhatsApp para enviados; recebido usa papel padrão
    return message.fromMe
      ? (isDark ? '#005c4b' : '#dcf8c6')
      : undefined;
  }, [message.fromMe, theme.palette]);

  // Cor de conteúdo para contraste sobre o fundo do container
  const contentColor = useMemo(() => {
    if (!message.fromMe) return undefined;
    try {
      const bg = (theme.palette?.type === 'dark' || theme.palette?.mode === 'dark') ? '#005c4b' : '#dcf8c6';
      return theme.palette.getContrastText ? theme.palette.getContrastText(bg) : undefined;
    } catch {
      return undefined;
    }
  }, [message.fromMe, theme.palette]);

  // 🎯 Renderização
  return (
  <div className={clsx(classes.audioPlayerContainer, message.fromMe ? classes.audioHoverRight : classes.audioHoverLeft)} style={{ backgroundColor: containerBg }}>
      {/* 🎵 Elemento de áudio - versão simplificada para PC */}
      {!isCurrentGlobalTrack && (
        <audio 
          ref={audioRef} 
          preload="metadata"
          playsInline={isIOS}
          onError={(e) => {
            console.error("❌ Erro no áudio PC:", {
              error: e,
              url: audioUrl,
              currentSrc: audioRef.current?.currentSrc,
              networkState: audioRef.current?.networkState,
              readyState: audioRef.current?.readyState
            });
          }}
          // onLoadStart={() => console.log("🎵 Iniciando carregamento áudio PC:", audioUrl)}
          // onCanPlay={() => console.log("🎵 Áudio PC pronto para reproduzir")}
          // onLoadedData={() => console.log("🎵 Dados do áudio PC carregados")}
        >
          {getAudioSource()}
          {!isIOS && <source src={audioUrl.replace('.ogg', '.mp3')} type="audio/mp3" />}
        </audio>
      )}
      
      {/* 🎯 Botão de play/pause */}
      <IconButton
        className={classes.playButton}
        onClick={togglePlayPause}
        disabled={isLoading || loadError}
      >
        {loadError ? (
          <ErrorOutline />
        ) : isLoading ? (
          <Speed />
        ) : isPlaying ? (
          <Pause />
        ) : (
          <PlayArrow />
        )}
      </IconButton>

      {/* 🎛️ Container dos controles */}
      <div className={classes.controlsContainer}>
        {loadError ? (
          // ❌ Estado de erro
          <div className={classes.errorContainer}>
            <ErrorOutline />
            <Typography variant="caption">{loadError}</Typography>
          </div>
        ) : isLoading && !isCurrentGlobalTrack ? (
          // 📊 Estado de loading
          <div className={classes.loadingContainer}>
            <LinearProgress style={{ flex: 1, height: 4 }} />
            <Typography variant="caption">Carregando...</Typography>
          </div>
        ) : (
          // 🎵 Controles normais
          <>
            {/* 🎚️ Barra de progresso */}
            <div className={classes.progressContainer}>
              <div
                ref={progressRef}
                className={`${classes.progressBar} ${classes.progressBarHover}`}
                onClick={handleProgressClick}
              >
                <div
                  className={classes.progressFill}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* ⏱️ Controles inferiores */}
            <div className={classes.bottomControls}>
              <Typography className={classes.timeText} style={contentColor ? { color: contentColor } : undefined}>
                {formatTime(isCurrentGlobalTrack ? (globalPlayer?.currentTime || 0) : currentTime)} / {formatTime(isCurrentGlobalTrack ? (globalPlayer?.duration || 0) : duration)}
              </Typography>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* ⚡ Velocidade */}
                <IconButton
                  className={classes.speedButton}
                  onClick={cyclePlaybackRate}
                  size="small"
                  disabled={isChangingSpeed}
                >
                  {(isCurrentGlobalTrack ? (globalPlayer?.playbackRate || playbackRate) : playbackRate)}x
                </IconButton>

                {/* ⏰ Hora + ✓✓ ACK dentro do player (apenas para enviados) */}
                {message && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, ...(contentColor ? { color: contentColor } : {}) }}>
                    {formatMessageTime && (
                      <Typography className={classes.timeText} style={contentColor ? { color: contentColor } : undefined}>
                        {formatMessageTime(message.createdAt)}
                      </Typography>
                    )}
                    <MessageAck message={message} classes={ackClasses} />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WhatsAppAudioPlayer;
